import { DOM } from '@levcharity/resources/scripts/helpers/DOM';

export class MyAccountDashboard {
    #dashboardData = {}
    #dashboard

    constructor() {
        this.#dashboard = DOM.el('.uh-dashboard-container');

        if (!this.#dashboard) {
            return;
        }

        this.#initProgressBars();
        this.#fetchData();
        this.#addEvents();
    }

    #initProgressBars(mainPercentage = null) {
        if (mainPercentage !== null && mainPercentage !== undefined) {
            const mainBar = DOM.el('.profile-progress-bar-wrapper .levcharity_progressbar', this.#dashboard);
            if (mainBar) {
                this.#applyProgressWidth(mainBar, mainPercentage);
            }
            return;
        }

        DOM.els(
            '.levcharity_progressbar[data-percentage], .levcharity_secondary_progressbar[data-percentage]',
            this.#dashboard
        ).forEach((progress) => {
            this.#applyProgressWidth(progress, progress.dataset.percentage ?? '0');
        });
    }

    #applyProgressWidth(progress, value) {
        const width = `${value}%`;
        progress.style.setProperty('--width', width);
        progress.style.width = width;
        progress.style.setProperty('--opacity', '1');
        progress.removeAttribute('data-percentage');
    }

    async #fetchData() {
        if (window.uh_dashboard_data) {
            this.#dashboardData = {
                participantData: window.uh_dashboard_data.participant_data,
                levelData:       window.uh_dashboard_data.level_data,
            };
            this.#renderProfile();
            return;
        }

        const { Helper } = await import('@levcharity/resources/scripts/helpers/Helper.js' /* webpackChunkName: "levcharity-helper" */);

        Helper.request(admin_ajax[0], {
            action: this.#dashboard.dataset.action,
            token:  this.#dashboard.dataset.token,
        }).then((response) => {
            if (response.success) {
                this.#dashboardData = {
                    participantData: response.participant_data,
                    levelData:       response.level_data,
                };
                this.#renderProfile();
            }
        }).catch((error) => {
            console.error('[Dashboard] AJAX failed:', error?.message ?? error);
        });
    }

    #renderProfile() {
        const { participantData, levelData } = this.#dashboardData;
        const currencySymbol = this.#dashboard.dataset.currencySymbol;

        const profileTotalsWrapper = DOM.el('.profile-totals-wrapper', this.#dashboard);

        const totalDonationCount = DOM.el('.total-donations .count', profileTotalsWrapper);
        const totalDonorsCount   = DOM.el('.total-donors .count', profileTotalsWrapper);

        this.#numberAnimation(participantData?.total_amount, totalDonationCount, currencySymbol);
        this.#numberAnimation(levelData?.donors_count, totalDonorsCount, '', '', 0, false);

        const progressBar            = DOM.el('.profile-progress-bar-wrapper', this.#dashboard);
        const currentCountEl         = DOM.el('.current-count', progressBar);
        const maxCountEl             = DOM.el('.max-count', progressBar);
        const nextBadgeCountEl       = DOM.el('.next-badge-count-wrapper .count', progressBar);

        this.#numberAnimation(levelData?.userTotalRaised, currentCountEl, currencySymbol);
        this.#numberAnimation(levelData?.userNextLevel?.[0]?.amountRange?.min, maxCountEl, currencySymbol);
        this.#numberAnimation(levelData?.userNeedToNextLevel, nextBadgeCountEl, currencySymbol);

        DOM.els('.profile-bucks-wrapper', this.#dashboard).forEach((wrapper) => {
            this.#numberAnimation(levelData?.userCoin, DOM.el('.count', wrapper), '', '', 0, false);
        });

        const progressTarget = levelData?.maxAmountRange || levelData?.userNextLevel?.[0]?.amountRange?.min || 0;
        const raised         = Number(levelData?.userTotalRaised) || 0;
        const target         = Number(progressTarget) || 0;
        const progressPercentage = target > 0 ? Math.min((raised / target) * 100, 100) : 0;

        this.#initProgressBars(progressPercentage);
    }

    async #numberAnimation(to, el, prefix = '', suffix = '', from = 0, withFractions = true) {
        if (!el || to == null) return;

        const { gsap } = await import('gsap' /* webpackChunkName: "gsap" */);

        gsap.to(
            { value: from },
            {
                value:    +to,
                duration: 0.8,
                ease:     'power2.inOut',
                onUpdate: function () {
                    el.textContent = prefix + this.targets()[0].value.toFixed(withFractions ? 2 : 0) + suffix;
                },
            }
        );
    }

    #addEvents() {
        this.#addShareEvents();
    }

    #addShareEvents() {
        const button = DOM.el('.share-with-friends', this.#dashboard);
        const panel  = DOM.el('.uh-dash-share-panel', this.#dashboard);

        if (!button || !panel) {
            return;
        }

        const nativeShare = DOM.el('.share.sharer', panel);

        const togglePanel = (expanded) => {
            panel.hidden = !expanded;
            button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        };

        DOM.addEvent('click', '.share-with-friends', (event) => {
            event.preventDefault();

            // The share block renders a hidden link that opens the device share sheet, so on
            // phones we hand over to it and get every installed app instead of our own list.
            if (typeof navigator.share === 'function' && nativeShare) {
                nativeShare.click();
                return;
            }

            togglePanel(panel.hidden);
        });

        document.addEventListener('click', (event) => {
            if (panel.hidden || panel.contains(event.target) || button.contains(event.target)) {
                return;
            }

            togglePanel(false);
        });

        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && !panel.hidden) {
                togglePanel(false);
                button.focus();
            }
        });
    }
}
